import {
  LoanPipelinesSignalREventType,
  LoanPipelinesSignalRMessageType,
} from 'constants/loanPipelines';

import {
  createLoanPipelineAction,
  deleteLoanPipelineAction,
  editLoanPipelineAction,
  fetchPipelineStatsIfEligibleAction,
  getLoanPipelinesAction,
  manageEntitiesChangedInPipelineAction,
  rebuiltLoanPipelineAction,
} from 'store/loanPipelines/actions';

class SignalRLoanPipelinesController {
  #connection;
  #dispatch;
  #logger;

  constructor(connection, logger) {
    this.#connection = connection;
    this.#logger = logger;
  }

  get connection() {
    if (!this.#connection) {
      this.#logger.error('Trying to use connection without active connection');
    }

    return this.#connection;
  }

  init(dispatch) {
    if (!dispatch) {
      this.#logger.error('Dispatch is required for initializing signalR loan pipelines controller');
    }

    this.#dispatch = dispatch;
  }

  onPageOpened() {
    this.#connection.invoke(LoanPipelinesSignalRMessageType.CONNECT_TO_LOANS);

    this.#startListeners();
  }

  onPageClosed() {
    this.#stopListeners();

    return this.#connection.invoke(LoanPipelinesSignalRMessageType.DISCONNECT_FROM_LOANS);
  }

  pingServer() {
    return this.#connection.invoke(LoanPipelinesSignalRMessageType.UPDATE_LOANS_CONNECTION);
  }

  #startListeners() {
    this.connection.on(
      LoanPipelinesSignalREventType.LOAN_PIPELINE_CREATED,
      this.#onLoanPipelineCreate.bind(this),
    );
    this.connection.on(
      LoanPipelinesSignalREventType.LOAN_PIPELINE_MODIFIED,
      this.#onLoanPipelineModify.bind(this),
    );
    this.connection.on(
      LoanPipelinesSignalREventType.LOAN_PIPELINE_DELETED,
      this.#onLoanPipelineDelete.bind(this),
    );
    this.connection.on(
      LoanPipelinesSignalREventType.LOAN_PIPELINE_REBUILT,
      this.#onLoanPipelineRebuilt.bind(this),
    );
    this.connection.on(
      LoanPipelinesSignalREventType.ENTITIES_CHANGED_IN_PIPELINE,
      this.#onEntitiesChangedInPipeline.bind(this),
    );
    this.connection.on(
      LoanPipelinesSignalREventType.LOAN_PIPELINES_REORDERED,
      this.#onLoanPipelinesReordered.bind(this),
    );
  }

  #stopListeners() {
    Object.values(LoanPipelinesSignalREventType).forEach(methodName =>
      this.connection.off(methodName),
    );
  }

  #onLoanPipelineCreate = loanPipeline => {
    this.#dispatch(createLoanPipelineAction(loanPipeline));
  };

  #onLoanPipelinesReordered() {
    this.#dispatch(getLoanPipelinesAction());
  }

  #onLoanPipelineModify(loanPipeline, countOfItems) {
    this.#dispatch(editLoanPipelineAction({ ...loanPipeline, countOfItems }));
  }

  #onLoanPipelineDelete(id) {
    this.#dispatch(deleteLoanPipelineAction(id));
  }

  #onLoanPipelineRebuilt(id, countOfItems) {
    this.#dispatch(rebuiltLoanPipelineAction({ id, countOfItems }));
    this.#dispatch(fetchPipelineStatsIfEligibleAction(id));
  }

  #onEntitiesChangedInPipeline(loanPipelineUpdateStatus) {
    this.#dispatch(
      editLoanPipelineAction({
        id: loanPipelineUpdateStatus.loanPipelineId,
        countOfItems: loanPipelineUpdateStatus.totalCount,
      }),
    );
    this.#dispatch(manageEntitiesChangedInPipelineAction(loanPipelineUpdateStatus));
    this.#dispatch(fetchPipelineStatsIfEligibleAction(loanPipelineUpdateStatus.loanPipelineId));
  }
}

export default SignalRLoanPipelinesController;
